import { siteText } from '../siteText'
import { useViewActive } from '../viewActivity'
import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react'
import { AlertCircle, ChevronDown, LoaderCircle, X } from 'lucide-react'

export function Button({ variant = 'primary', size = 'md', busy, children, ...props }: ComponentProps<'button'> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'md'; busy?: boolean }) {
  return (
    <button className={`button button-${variant} button-${size}`} disabled={props.disabled || busy} {...props}>
      {busy ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : null}
      {siteText(children)}
    </button>
  )
}

export function IconButton({ label, children, ...props }: ComponentProps<'button'> & { label: string }) {
  return <button className="icon-button" aria-label={siteText(label)} title={siteText(label)} {...props}>{siteText(children)}</button>
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span className="field-label">{siteText(label)}</span>{siteText(children)}{hint ? <span className="field-hint">{siteText(hint)}</span> : null}</label>
}

export function TextInput(props: ComponentProps<'input'>) {
  return <input className="text-input" {...props} />
}

export function TextArea(props: ComponentProps<'textarea'>) {
  return <textarea className="text-area" {...props} />
}

export function Select(props: ComponentProps<'select'>) {
  return <span className="select-wrap"><select className="select" {...props} /></span>
}

export function StatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase().replaceAll('_', ' ')
  return <span className={`status status-${status.toLowerCase().replaceAll('_', '-')}`}><span className="status-dot" />{siteText(normalized)}</span>
}

export function Notice({ tone = 'error', children, onDismiss }: { tone?: 'error' | 'success' | 'info'; children: ReactNode; onDismiss?: () => void }) {
  if (tone === 'success') return <span className="sr-only" role="status">{siteText(children)}</span>
  return <div className={`notice notice-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
    <AlertCircle size={17} />
    <span>{siteText(children)}</span>
    {onDismiss ? <IconButton label="Dismiss" onClick={onDismiss}><X size={16} /></IconButton> : null}
  </div>
}

export function WorkspaceLoading({ label = 'Loading workspace' }: { label?: string }) {
  return <div className="workspace-page-loading" role="status" aria-label={siteText(label)}><span className="motion-logo workspace-loading-logo" aria-hidden="true" /><span className="sr-only">{siteText(label)}</span></div>
}

export function WorkspaceEmpty({ label = 'No content yet' }: { label?: string }) {
  return <div className="workspace-page-empty" role="status" aria-label={siteText(label)}><span className="motion-logo" aria-hidden="true" /><span className="sr-only">{siteText(label)}</span></div>
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return <div className="loading-state"><LoaderCircle className="spin" size={22} /><span>{siteText(label)}</span></div>
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty-state">{icon ? <div className="empty-icon">{siteText(icon)}</div> : null}<h3>{siteText(title)}</h3>{children ? <p>{siteText(children)}</p> : null}{siteText(action)}</div>
}

export function Modal({ title, children, onClose, width = '520px', className, hideTitle = false, closeDisabled = false, headerContent }: { headerContent?: ReactNode; hideTitle?: boolean; closeDisabled?: boolean; title: string; children: ReactNode; onClose: () => void; width?: string; className?: string }) {
  const active = useViewActive()
  const dialog = useRef<HTMLElement>(null)
  const close = useRef(onClose)
  close.current = () => { if (!closeDisabled) onClose() }
  useEffect(() => {
    if (!active) return
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const frame = window.requestAnimationFrame(() => {
      (dialog.current?.querySelector<HTMLElement>('[data-modal-autofocus]') ||
        dialog.current?.querySelector<HTMLElement>('.sources-file-choice') ||
        dialog.current?.querySelector<HTMLElement>('input:not([hidden]), select, textarea, button'))?.focus()
    })
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); close.current(); return }
      if (event.key !== 'Tab' || !dialog.current) return
      const focusable = Array.from(dialog.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled):not([hidden]), select:not(:disabled), textarea:not(:disabled), a[href]'))
        .filter((element) => element.getClientRects().length > 0)
      const first = focusable[0], last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && (document.activeElement === first || !dialog.current.contains(document.activeElement))) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.current.contains(document.activeElement))) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true })
    }
  }, [active])
  if (!active) return null
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close.current() }}>
    <section ref={dialog} className={`modal${className ? ` ${className}` : ''}`} role="dialog" aria-modal="true" aria-label={siteText(title)} style={{ maxWidth: width }}>
      <div className="modal-header">{headerContent}{!hideTitle ? <h2>{siteText(title)}</h2> : null}<IconButton label="Close" disabled={closeDisabled} onClick={() => close.current()}><X size={18} /></IconButton></div>
      <div className="modal-body">{siteText(children)}</div>
    </section>
  </div>
}

export function Disclosure({ title, open, onToggle, children, count }: { title: string; open: boolean; onToggle: () => void; children: ReactNode; count?: string }) {
  return <div className="disclosure"><button className="disclosure-toggle" onClick={onToggle} aria-expanded={open}><span>{siteText(title)}</span>{count ? <span className="disclosure-count">{siteText(count)}</span> : null}<ChevronDown className={open ? 'rotate-180' : ''} size={18} /></button>{open ? <div className="disclosure-content">{siteText(children)}</div> : null}</div>
}

export function formatDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return 'Not available'
  const total = Math.max(0, Math.round(seconds))
  const min = Math.floor(total / 60)
  const sec = String(total % 60).padStart(2, '0')
  return `${min}:${sec}`
}

export function formatDate(date: string | null | undefined, includeTime = false) {
  if (!date) return 'Not available'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.valueOf())) return date
  return new Intl.DateTimeFormat(undefined, includeTime ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric', year: 'numeric' }).format(parsed)
}

export function humanize(value: string | null | undefined) {
  if (!value) return 'Not available'
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}
